// Календарь инженеров — кто на каком судне и в какие дни. ТОЛЬКО ЧТЕНИЕ.
//
// ЧТО ЭТОТ ЭКРАН ОТВЕЧАЕТ. Один вопрос, ради которого его открывают перед тем, как обещать
// клиенту дату: свободен ли человек в этот день и чем он занят, если нет. Всё остальное —
// суммы, стадии, состав работ — здесь только для того, чтобы полоса читалась.
//
// ЧТО ОН ЗАМЕНЯЕТ. Та же страница до сих пор собиралась ОФЛАЙН на ноутбуке владельца и
// клалась готовым HTML в «брифинг». Сети в том сборщике нет: пока ноутбук закрыт, календарь
// стоит. Поля, из которых он строит полосы, лежат в зеркале, поэтому раздел строится прямо
// из базы и обновляется вместе с синком (см. sched_api.py).
//
// ОТПУСКА ЗДЕСЬ ЕСТЬ — с 29.07.2026. Раньше их не было, и экран честно об этом писал:
// пустая строка значила «нет работ», а не «человек на месте». Теперь ядро отдаёт слой
// отсутствий (отпуск, больничный, командировка, отгул) из графика Bitrix — того же
// источника, что и раздел «Отсутствия». Это меняет главное: «свободные» и «отсутствующие»
// стали РАЗНЫМИ списками, а раньше человек в отпуске стоял среди свободных, и календарь
// буквально предлагал поставить его на судно.
//
// ЧЕГО ЭКРАН НЕ ПОКАЗЫВАЕТ НАМЕРЕННО. Причину отсутствия — только вид и даты. Рядом с
// видом «больничный» причина это сведения о здоровье, и в календаре планирования ей делать
// нечего; ядро её и не отдаёт. Оговорки про всё это приезжают данными, а не вписаны сюда.
//
// КОНФЛИКТ — не ошибка счёта. Человек может одновременно стоять на работе и числиться
// отсутствующим: это расхождение двух источников, и правится оно в Bitrix. Экран обязан
// его показать, а не выбрать одну из двух правд молча.
//
// ПОЧЕМУ ГАНТ НЕ ТАБЛИЦА. Колонок-дней три с лишним десятка, и на телефоне таблица с
// data-label превратилась бы в столбик из тридцати с лишним подписей на каждую строку.
// Поэтому полосы — прокручиваемая сетка, а рядом обычная таблица работ: она и отвечает на
// «что именно за работа», и разбирается на карточки по общему правилу.
//
// ЧИСЕЛ-ЗАМЕРОВ в этой шапке нет намеренно: любое вписанное сюда количество устареет молча.
// Всё, что про объём данных, экран печатает из ответа ядра.
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { CoreUnsupported, Unauthorized } from '../api/client'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtMoney, pluralN } from '../lib/format'

// ─────────────────────────── что отдаёт ядро ───────────────────────────

interface Работа {
  deal_id: number
  title: string
  ship: string
  port: string
  imo: string
  stage: string
  stage_name: string
  wtype: string
  wtype_name: string
  /** Границы полосы ПОСЛЕ обрезки окном. Настоящие даты — в plan_start/plan_end. */
  start: string
  end: string
  plan_start: string
  plan_end: string
  /** Смещение и длина в днях окна: ширину дня решает экран, а не ядро. */
  off: number
  days: number
  clipped_left: boolean
  clipped_right: boolean
  amount: number | null
  currency: string
  lane: number
}

/**
 * Полоса отсутствия. Поля границ и обрезки те же, что у работы, — экран кладёт их в одну
 * сетку дней, и разъехавшиеся системы координат он бы не заметил, а человек заметил бы.
 * Причины здесь нет и не будет: ядро её не отдаёт (см. шапку файла).
 */
interface Отсутствие {
  kind: string
  kind_name: string
  start: string
  end: string
  plan_start: string
  plan_end: string
  off: number
  days: number
  clipped_left: boolean
  clipped_right: boolean
}

/** Что ядро говорит про отсутствия одного человека. Общее для занятых и отсутствующих. */
interface СОтсутствиями {
  absences: Отсутствие[]
  absent_days: number
  absence_kinds: string[]
  /** Дни, где работа наложилась на отсутствие. Расхождение источников, не ошибка счёта. */
  conflict_days: number
  conflict_first: number | null
}

interface Строка extends СОтсутствиями {
  engineer_id: number
  name: string
  position: string
  city: string
  active: boolean
  known: boolean
  jobs: Работа[]
  lanes: number
  jobs_count: number
  busy_days: number
  first_day: string
}

interface Свободный {
  engineer_id: number
  name: string
  position: string
  city: string
}

/** Полевой без работ в окне, но в отпуске, командировке, отгуле или на больничном. */
type Отсутствующий = Свободный & СОтсутствиями

interface День {
  date: string
  wd: string
  day: number
  month: number
  weekend: boolean
  today: boolean
}

interface Окно {
  from: string
  to: string
  today: string
  back: number
  fwd: number
  days_count: number
  days: День[]
}

interface ВидОтсутствия {
  code: string
  name: string
  rows: number
  people?: number
  today?: number
}

interface Доска {
  window: Окно
  rows: Строка[]
  free: Свободный[]
  away: Отсутствующий[]
  unassigned: Работа[]
  absence_kinds: ВидОтсутствия[]
  totals: {
    jobs_in_window: number
    jobs: number
    jobs_shown: number
    engineers: number
    busy: number
    free: number
    away: number
    unassigned: number
    field_users: number
    absence_rows: number
    absence_people: number
    conflicts: number
  }
  trimmed: boolean
  jobs_limit: number
  notes: Record<string, string>
}

interface Справочники {
  stages: Array<{ code: string; name: string; deals: number }>
  work_types: Array<{ code: string; name: string }>
  window: { back: number; fwd: number; back_max: number; fwd_max: number }
  counts: {
    deals: number
    with_engineer: number
    with_start: number
    with_both_dates: number
    field_users: number
    absence_rows: number
    absent_today: number
  }
  absence_kinds: ВидОтсутствия[]
  engineers: Свободный[]
  sync: Array<{ entity: string; last_sync_at: string | null; rows: number | null }>
  last_deal_change: string | null
  notes: Record<string, string>
}

// ─────────────────────────── транспорт ───────────────────────────
// Свой fetch, а не общий api/client: там эмуляция Bitrix REST (POST /api/bx с method),
// здесь обычная GET-ручка ядра со своими фильтрами. Разбор ответа — тот же, что у
// инженерного блока: 401 уводит на вход, 400 поднимается отдельным типом, чтобы QueryState
// напечатал «такой фильтр обслужить нельзя» вместо пустого календаря.

const БАЗА = '/api/sched'

async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

function строкаЗапроса(ф: Record<string, string | number | boolean | undefined>): string {
  const п = new URLSearchParams()
  for (const [к, з] of Object.entries(ф)) {
    if (з === undefined || з === '' || з === false) continue
    п.set(к, String(з))
  }
  const s = п.toString()
  return s ? `?${s}` : ''
}

// ─────────────────────────── размеры полос ───────────────────────────
// Ширина дня и высота полосы — про раскладку, а не про данные, поэтому живут здесь.

const ДЕНЬ_ШИР = 34
const ИМЯ_ШИР = 190
const ПОЛОСА_ВЫС = 22
/** Полоса отсутствия ниже полосы работы: она про «нельзя», а не про «что именно». */
const ОТСУТСТВИЕ_ВЫС = 10
const ЗАЗОР = 3

/** Цвет полосы по стадии. Стадии приходят из ядра, цвет — дело экрана. */
const ЦВЕТ_СТАДИИ: Record<string, string> = {
  'C4:PREPARATION': 'var(--muted)',
  'C4:7': 'var(--accent)',
  'C4:8': 'var(--faint)',
  'C4:EXECUTING': 'var(--warn-text)',
  'C4:9': 'var(--lose)',
  'C4:PREPAYMENT_INVOICE': 'var(--accent)',
  'C4:FINAL_INVOICE': 'var(--muted)',
  'C4:UC_9KDMWI': 'var(--won)',
}

function цвет(стадия: string): string {
  return ЦВЕТ_СТАДИИ[стадия] ?? 'var(--muted)'
}

/**
 * Цвет полосы отсутствия. Больничный намеренно самый тихий из четырёх: вид назвать надо,
 * кричать им на весь экран — не надо.
 */
const ЦВЕТ_ОТСУТСТВИЯ: Record<string, string> = {
  'отпуск': 'var(--won)',
  'командировка': 'var(--accent)',
  'отгул': 'var(--muted)',
  'больничный': 'var(--faint)',
}

function цветОтсутствия(вид: string): string {
  return ЦВЕТ_ОТСУТСТВИЯ[вид] ?? 'var(--faint)'
}

/**
 * Штриховка, а не заливка. Полоса отсутствия обязана отличаться от полосы работы не только
 * цветом: цвет — единственный признак, который не работает у дальтоника и на чёрно-белой
 * распечатке, а спутать «занят» с «нельзя ставить» здесь дороже всего.
 */
function штриховка(цвет: string): string {
  return `repeating-linear-gradient(135deg, ${цвет} 0 6px, transparent 6px 12px)`
}

/**
 * Полосы отсутствия по дорожкам: две в один день не должны лечь друг на друга. Ядро
 * раскладывает по дорожкам только работы — у отсутствий это редкий случай (отпуск и отгул
 * подряд), и гонять ради него ещё один проход по базе незачем.
 */
function дорожкиОтсутствий(полосы: Отсутствие[]): Array<Отсутствие & { lane: number }> {
  const концы: number[] = []
  return [...полосы]
    .sort((a, b) => a.off - b.off || a.days - b.days)
    .map((п) => {
      let дорожка = концы.findIndex((конец) => конец <= п.off)
      if (дорожка < 0) дорожка = концы.length
      концы[дорожка] = п.off + п.days
      return { ...п, lane: дорожка }
    })
}

function подсказкаОтсутствия(п: Отсутствие): string {
  const края = [
    п.clipped_left ? 'начало раньше окна' : '',
    п.clipped_right ? 'конец позже окна' : '',
  ].filter(Boolean).join(', ')
  return [
    п.kind_name,
    `${fmtDate(п.plan_start)} — ${fmtDate(п.plan_end)}`,
    'причина не показывается: рядом с больничным это врачебная тайна',
    края,
  ].filter(Boolean).join('\n')
}

/** Сводка «отпуск, командировка» для подписи под именем и в таблицах. */
function видыСловами(человек: СОтсутствиями, виды: ВидОтсутствия[]): string {
  const имена = new Map(виды.map((в) => [в.code, в.name]))
  return человек.absence_kinds
    .map((к) => (имена.get(к) ?? к).toLowerCase())
    .join(', ')
}

// ═══════════════════════════════ экран ═══════════════════════════════

export default function EngineerSchedule() {
  const [engineer, setEngineer] = useState('')
  const [stage, setStage] = useState('')
  const [wtype, setWtype] = useState('')
  const [q, setQ] = useState('')
  const [fwd, setFwd] = useState('30')
  const [free, setFree] = useState(false)

  const справочники = useQuery({
    queryKey: ['sched', 'refs'],
    queryFn: () => запрос<Справочники>('/refs'),
    retry: 1,
    staleTime: 60_000,
  })

  const фильтры = useMemo(() => ({
    stage: stage || undefined,
    engineer: engineer || undefined,
    wtype: wtype || undefined,
    q: q.trim() || undefined,
    fwd,
  }), [stage, engineer, wtype, q, fwd])

  const доска = useQuery({
    queryKey: ['sched', 'board', фильтры],
    queryFn: () => запрос<Доска>('/board' + строкаЗапроса(фильтры)),
    placeholderData: (prev) => prev,
    retry: 1,
    staleTime: 60_000,
  })

  const д = доска.data
  const сп = справочники.data
  const дни = д?.window.days ?? []
  const оговорки = д?.notes ?? сп?.notes ?? {}

  return (
    <>
      <div className="page-head">
        <h1>Календарь инженеров</h1>
        <span className="spacer" />
        <span className="hint">
          {д
            ? `${fmtDate(д.window.from)} — ${fmtDate(д.window.to)} · занято `
              + `${pluralN(д.totals.busy, 'человек', 'человека', 'человек')}`
            : ''}
        </span>
      </div>

      <div className="notice">
        <b>Отпуска, больничные, командировки и отгулы учтены.</b>{' '}
        {оговорки.absences
          ?? 'Они берутся из графика отсутствий Bitrix — того же источника, что и раздел '
             + '«Отсутствия». Пустая строка человека значит «нет ни работ, ни отсутствий '
             + 'в этом окне».'}{' '}
        {оговорки.absences_privacy}
      </div>

      <QueryState isLoading={доска.isLoading} error={доска.error ?? справочники.error}>
        {д && (
          <>
            <Плитки д={д} />

            <div className="card" style={{ marginBottom: 16 }}>
              <FilterBar id="engineer-schedule">
                <div className="f" style={{ flex: 2, minWidth: 220 }}>
                  <label htmlFor="esq">Судно, порт или номер IMO</label>
                  <input id="esq" value={q} onChange={(e) => setQ(e.target.value)}
                         placeholder="например: Балтика" />
                </div>

                <div className="f">
                  <label htmlFor="ese">Инженер</label>
                  <select id="ese" value={engineer}
                          onChange={(e) => setEngineer(e.target.value)}>
                    <option value="">все</option>
                    {(сп?.engineers ?? []).map((и) => (
                      <option key={и.engineer_id} value={String(и.engineer_id)}>{и.name}</option>
                    ))}
                  </select>
                </div>

                <div className="f">
                  <label htmlFor="ess">Стадия работы</label>
                  <select id="ess" value={stage} onChange={(e) => setStage(e.target.value)}>
                    <option value="">все живые</option>
                    {(сп?.stages ?? []).map((с) => (
                      <option key={с.code} value={с.code}>{с.name} ({с.deals})</option>
                    ))}
                  </select>
                </div>

                <div className="f">
                  <label htmlFor="esw">Тип работ</label>
                  <select id="esw" value={wtype} onChange={(e) => setWtype(e.target.value)}>
                    <option value="">любой</option>
                    {(сп?.work_types ?? []).map((т) => (
                      <option key={т.code} value={т.code}>{т.name}</option>
                    ))}
                  </select>
                </div>

                <div className="f">
                  <label htmlFor="esf">Насколько вперёд</label>
                  <select id="esf" value={fwd} onChange={(e) => setFwd(e.target.value)}>
                    <option value="14">две недели</option>
                    <option value="30">месяц</option>
                    <option value="60">два месяца</option>
                    <option value="90">три месяца</option>
                  </select>
                </div>

                <div className="f">
                  <label htmlFor="esfree">Кто без работ</label>
                  <select id="esfree" value={free ? 'y' : 'n'}
                          onChange={(e) => setFree(e.target.value === 'y')}>
                    <option value="n">не показывать</option>
                    <option value="y">свободные и отсутствующие</option>
                  </select>
                </div>
              </FilterBar>
            </div>

            <Гант д={д} дни={дни} выбрать={(id) => setEngineer(String(id))} />

            {д.trimmed && (
              <div className="notice">
                Показаны не все полосы: ответ обрезан пределом{' '}
                {pluralN(д.jobs_limit, 'работы', 'работ', 'работ')}. Сузьте окно или фильтр —
                иначе календарь врёт полнотой.
              </div>
            )}

            <Работы д={д} />

            {free && <Свободные люди={д.free} />}
            {free && <Отсутствуют люди={д.away} виды={д.absence_kinds}
                                  оговорки={оговорки} />}

            <Границы д={д} сп={сп} оговорки={оговорки} />
          </>
        )}
      </QueryState>
    </>
  )
}

// ═══════════════════════════════ плитки ═══════════════════════════════

function Плитки({ д }: { д: Доска }) {
  const плитки: Array<[string, number, string]> = [
    ['Занято людей', д.totals.busy, 'у кого есть хотя бы одна работа в окне'],
    ['Свободно', д.totals.free,
      'полевые сотрудники без работ и без отсутствий в окне'],
    ['Отсутствуют', д.totals.away,
      'полевые без работ, но в отпуске, командировке, отгуле или на больничном'],
    ['Работ в окне', д.totals.jobs_in_window, 'строк календаря до фильтров'],
    ['Без исполнителя', д.totals.unassigned, 'работа есть, человек не назначен'],
    ['Конфликтов', д.totals.conflicts,
      'человек стоит на работе и одновременно числится отсутствующим: '
      + 'расхождение двух источников, правится в Bitrix'],
  ]
  return (
    <div className="tiles" style={{ marginBottom: 16 }}>
      {плитки.map(([имя, число, смысл]) => (
        <div key={имя} className="tile" title={смысл}>
          <div className="hint">{имя}</div>
          <div className="t-sum">{число}</div>
        </div>
      ))}
    </div>
  )
}

// ═══════════════════════════════ гант ═══════════════════════════════

function Гант({ д, дни, выбрать }: {
  д: Доска
  дни: День[]
  выбрать: (id: number) => void
}) {
  const ширина = дни.length * ДЕНЬ_ШИР
  if (д.rows.length === 0) {
    return (
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="empty">
          <div className="empty-title">В это окно не попала ни одна работа</div>
          <div className="empty-hint">
            Это ответ про фильтры и границы окна, а не про инженеров: раздвиньте окно или
            снимите условие. Отсутствия при этом всё равно посчитаны — их{' '}
            {pluralN(д.totals.absence_rows, 'строка', 'строки', 'строк')} на{' '}
            {pluralN(д.totals.absence_people, 'человека', 'человек', 'человек')}.
          </div>
        </div>
      </div>
    )
  }
  return (
    <div className="card" style={{ marginBottom: 16, overflowX: 'auto' }}>
      <div style={{ minWidth: ИМЯ_ШИР + ширина }}>
        <div style={{ display: 'flex', alignItems: 'flex-end',
                      borderBottom: '1px solid var(--border-strong)' }}>
          <div style={{ width: ИМЯ_ШИР, flex: '0 0 auto', paddingBottom: 4 }}>
            <span className="hint">Инженер</span>
          </div>
          {дни.map((день) => (
            <div key={день.date} className="hint"
                 style={{
                   width: ДЕНЬ_ШИР, flex: '0 0 auto', paddingBottom: 4,
                   background: день.today ? 'var(--accent-soft)'
                     : день.weekend ? 'var(--surface-2)' : 'transparent',
                   color: день.today ? 'var(--accent)' : undefined,
                 }}>
              <div>{день.wd}</div>
              <div>{день.day}</div>
            </div>
          ))}
        </div>

        {д.rows.map((р) => (
          <div key={р.engineer_id}
               style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
            <div style={{ width: ИМЯ_ШИР, flex: '0 0 auto', padding: '6px 8px 6px 0' }}>
              {/* Кнопка, а не ссылка: своей страницы у человека нет, нажатие сужает
                  календарь до него. Вид даёт .btn-link из styles.css — обычная
                  кнопка распирала бы строку Ганта. */}
              <button type="button" className="btn-link"
                      onClick={() => выбрать(р.engineer_id)}
                      title={`${р.position || 'должность не указана'} · ${
                        р.city || 'город не указан'}`}>
                {р.name}
              </button>
              <div className="hint">
                {pluralN(р.busy_days, 'день', 'дня', 'дней')} занят
              </div>
              {р.absences.length > 0 && (
                <div className="hint">
                  {видыСловами(р, д.absence_kinds)}:{' '}
                  {pluralN(р.absent_days, 'день', 'дня', 'дней')}
                </div>
              )}
              {р.conflict_days > 0 && (
                /* --err, а не --lose: страница говорит смыслом («это ошибка данных»),
                   цвет за смыслом закреплён в карте статусов styles.css. */
                <div className="hint" style={{ color: 'var(--err)' }}
                     title="Работа поставлена на дни, в которые человек числится
                            отсутствующим. Расхождение источников — правится в Bitrix.">
                  конфликт: {pluralN(р.conflict_days, 'день', 'дня', 'дней')}
                </div>
              )}
            </div>
            <ДорожкиЧеловека строка={р} дни={дни} />
          </div>
        ))}
        <Легенда виды={д.absence_kinds} />
      </div>
    </div>
  )
}

/**
 * Что означают штрихованные полосы. Без подписи они читаются как ещё один статус работы,
 * а это ровно противоположное: человека на судно ставить нельзя.
 */
function Легенда({ виды }: { виды: ВидОтсутствия[] }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, paddingTop: 8 }}>
      <span className="hint">Отсутствия:</span>
      {виды.map((в) => (
        <span key={в.code} className="hint"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            width: 22, height: 10, borderRadius: 3,
            background: штриховка(цветОтсутствия(в.code)),
          }} />
          {в.name}
        </span>
      ))}
    </div>
  )
}

function ДорожкиЧеловека({ строка, дни }: { строка: Строка; дни: День[] }) {
  // Отсутствия кладутся ПОД работы, своими дорожками: наложи их друг на друга — и
  // конфликт «поставлен на судно в отпуске» скроется ровно там, где его надо видеть.
  const отс = useMemo(() => дорожкиОтсутствий(строка.absences), [строка.absences])
  const дорожекОтс = отс.reduce((м, п) => Math.max(м, п.lane + 1), 0)
  const высотаОтс = дорожекОтс > 0 ? дорожекОтс * (ОТСУТСТВИЕ_ВЫС + ЗАЗОР) : 0
  const высота = строка.lanes * (ПОЛОСА_ВЫС + ЗАЗОР) + ЗАЗОР + высотаОтс
  return (
    <div style={{ position: 'relative', height: высота, flex: '1 1 auto',
                  minWidth: дни.length * ДЕНЬ_ШИР }}>
      {/* Подложка: выходные и сегодня видно и там, где полос нет. */}
      <div style={{ display: 'flex', position: 'absolute', inset: 0 }}>
        {дни.map((день) => (
          <div key={день.date}
               style={{
                 width: ДЕНЬ_ШИР, flex: '0 0 auto',
                 borderRight: '1px solid var(--border)',
                 background: день.today ? 'var(--accent-soft)'
                   : день.weekend ? 'var(--surface-2)' : 'transparent',
               }} />
        ))}
      </div>
      {строка.jobs.map((р) => (
        <Link key={`${р.deal_id}-${р.lane}-${р.off}`} to={`/deals/${р.deal_id}`}
              title={подсказка(р)}
              style={{
                position: 'absolute',
                left: р.off * ДЕНЬ_ШИР + 1,
                width: Math.max(р.days * ДЕНЬ_ШИР - 2, 10),
                top: р.lane * (ПОЛОСА_ВЫС + ЗАЗОР) + ЗАЗОР,
                height: ПОЛОСА_ВЫС,
                background: цвет(р.stage),
                color: 'var(--surface)',
                borderRadius: 4,
                padding: '0 6px',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                lineHeight: `${ПОЛОСА_ВЫС}px`,
                textDecoration: 'none',
                borderLeft: р.clipped_left ? '3px dotted var(--surface)' : undefined,
                borderRight: р.clipped_right ? '3px dotted var(--surface)' : undefined,
              }}>
          {р.ship || р.title}
        </Link>
      ))}
      {отс.map((п) => (
        <div key={`${п.kind}-${п.start}-${п.lane}`} title={подсказкаОтсутствия(п)}
             style={{
               position: 'absolute',
               left: п.off * ДЕНЬ_ШИР + 1,
               width: Math.max(п.days * ДЕНЬ_ШИР - 2, 10),
               top: строка.lanes * (ПОЛОСА_ВЫС + ЗАЗОР) + ЗАЗОР
                    + п.lane * (ОТСУТСТВИЕ_ВЫС + ЗАЗОР),
               height: ОТСУТСТВИЕ_ВЫС,
               background: штриховка(цветОтсутствия(п.kind)),
               border: `1px solid ${цветОтсутствия(п.kind)}`,
               borderRadius: 3,
               borderLeftStyle: п.clipped_left ? 'dotted' : 'solid',
               borderRightStyle: п.clipped_right ? 'dotted' : 'solid',
             }} />
      ))}
    </div>
  )
}

function подсказка(р: Работа): string {
  const края = [
    р.clipped_left ? 'начало раньше окна' : '',
    р.clipped_right ? 'конец позже окна' : '',
  ].filter(Boolean).join(', ')
  return [
    `${р.ship || р.title}`,
    `${fmtDate(р.plan_start)} — ${fmtDate(р.plan_end)}`,
    р.port ? `порт: ${р.port}` : '',
    `${р.stage_name} · ${р.wtype_name}`,
    края,
  ].filter(Boolean).join('\n')
}

// ═══════════════════════════════ таблица работ ═══════════════════════════════

function Работы({ д }: { д: Доска }) {
  const строки: Array<{ имя: string; работа: Работа }> = []
  for (const р of д.rows) for (const ж of р.jobs) строки.push({ имя: р.name, работа: ж })
  for (const ж of д.unassigned) строки.push({ имя: '', работа: ж })
  строки.sort((a, b) => a.работа.start.localeCompare(b.работа.start)
    || a.работа.deal_id - b.работа.deal_id)

  return (
    <div className="card">
      <h2>Работы окна списком</h2>
      {/* `cards` обязателен: на телефоне таблица разбирается на карточки (styles.css §12),
          подписи берутся из data-label. */}
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Инженер</th>
              <th>Судно</th>
              <th className="nowrap">Начало</th>
              <th className="nowrap">Конец</th>
              <th className="num">Дней</th>
              <th className="nowrap">Стадия</th>
              <th className="num">Сумма</th>
              <th className="nowrap">Сделка</th>
            </tr>
          </thead>
          <tbody>
            {строки.map(({ имя, работа }) => (
              <tr key={`${работа.deal_id}-${имя}`}>
                <td data-label="Инженер">
                  {имя || <span className="muted">исполнитель не назначен</span>}
                </td>
                {/* Название сделки целиком — в подсказке: судно вытащено из него же,
                    и человек должен иметь возможность увидеть, из чего именно. */}
                <td data-label="Судно" title={работа.title}>
                  {работа.ship || <span className="muted">без названия</span>}
                  {работа.imo && <div className="muted mono">IMO {работа.imo}</div>}
                  {работа.port && <div className="hint">{работа.port}</div>}
                </td>
                <td className="nowrap" data-label="Начало">
                  {fmtDate(работа.plan_start)}
                  {работа.clipped_left && <div className="hint">раньше окна</div>}
                </td>
                <td className="nowrap" data-label="Конец">
                  {fmtDate(работа.plan_end)}
                  {работа.clipped_right && <div className="hint">позже окна</div>}
                </td>
                <td className="num" data-label="Дней">{работа.days}</td>
                <td className="nowrap" data-label="Стадия">
                  <span className="badge work">{работа.stage_name}</span>
                  <div className="hint">{работа.wtype_name}</div>
                </td>
                <td className="num" data-label="Сумма">
                  {работа.amount === null
                    ? <span className="muted">не указана</span>
                    : fmtMoney(работа.amount, работа.currency || 'RUB')}
                </td>
                <td className="nowrap" data-label="Сделка">
                  <Link to={`/deals/${работа.deal_id}`}>{работа.deal_id}</Link>
                </td>
              </tr>
            ))}
            {строки.length === 0 && (
              <tr>
                <td colSpan={8} className="empty">
                  <div className="empty-title">Под эти условия работ нет</div>
                  <div className="empty-hint">
                    Это ответ про фильтры и окно, а не про занятость: снимите условие,
                    и список вернётся.
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ═══════════════════════════════ свободные ═══════════════════════════════

function Свободные({ люди }: { люди: Свободный[] }) {
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h2>Свободны в этом окне</h2>
      <div className="notice">
        Ни работ, ни отпуска, ни командировки в окне. Список считается по должности из
        профиля Bitrix: неверно записанная должность выкидывает человека отсюда — поэтому
        это подсказка, кого спросить, а не наряд.
      </div>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Инженер</th>
              <th>Должность</th>
              <th>Город</th>
            </tr>
          </thead>
          <tbody>
            {люди.map((ч) => (
              <tr key={ч.engineer_id}>
                <td data-label="Инженер">{ч.name}</td>
                <td data-label="Должность">
                  {ч.position || <span className="muted">не указана</span>}
                </td>
                <td data-label="Город">
                  {ч.city || <span className="muted">не указан</span>}
                </td>
              </tr>
            ))}
            {люди.length === 0 && (
              <tr>
                <td colSpan={3} className="empty">
                  <div className="empty-title">Свободных в этом окне нет</div>
                  <div className="empty-hint">
                    Все полевые сотрудники, которых знает справочник, либо заняты хотя бы
                    один день, либо отсутствуют. Раздвиньте окно, чтобы увидеть, когда
                    освободятся.
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ═══════════════════════════════ отсутствующие ═══════════════════════════════

/**
 * Полевые без работ, но в отпуске, командировке, отгуле или на больничном. Отдельной
 * таблицей, а не строкой в «свободных»: раньше эти люди стояли ровно там, и календарь
 * предлагал поставить их на судно.
 */
function Отсутствуют({ люди, виды, оговорки }: {
  люди: Отсутствующий[]
  виды: ВидОтсутствия[]
  оговорки: Record<string, string>
}) {
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h2>Отсутствуют в этом окне</h2>
      <div className="notice">
        Работ в окне у них нет, но поставить их на судно нельзя.{' '}
        {оговорки.absences_privacy
          ?? 'Причина отсутствия не показывается — только вид и даты.'}
      </div>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Инженер</th>
              <th>Должность</th>
              <th>Город</th>
              <th>Отсутствие</th>
              <th className="nowrap">С какого</th>
              <th className="nowrap">По какое</th>
              <th className="num">Дней</th>
            </tr>
          </thead>
          <tbody>
            {люди.map((ч) => (
              <tr key={ч.engineer_id}>
                <td data-label="Инженер">{ч.name}</td>
                <td data-label="Должность">
                  {ч.position || <span className="muted">не указана</span>}
                </td>
                <td data-label="Город">
                  {ч.city || <span className="muted">не указан</span>}
                </td>
                <td data-label="Отсутствие">
                  {ч.absences.map((п) => (
                    <div key={`${п.kind}-${п.start}`}>
                      <span className="badge work">{п.kind_name}</span>
                    </div>
                  ))}
                </td>
                <td className="nowrap" data-label="С какого">
                  {ч.absences.map((п) => (
                    <div key={`${п.kind}-${п.start}`}>
                      {fmtDate(п.plan_start)}
                      {п.clipped_left && <div className="hint">раньше окна</div>}
                    </div>
                  ))}
                </td>
                <td className="nowrap" data-label="По какое">
                  {ч.absences.map((п) => (
                    <div key={`${п.kind}-${п.start}`}>
                      {fmtDate(п.plan_end)}
                      {п.clipped_right && <div className="hint">позже окна</div>}
                    </div>
                  ))}
                </td>
                {/* Дней ОКНА, а не длина отпуска: два отсутствия внахлёст дают один день,
                    а не два, и складывать их длины было бы враньём. */}
                <td className="num" data-label="Дней" title={видыСловами(ч, виды)}>
                  {ч.absent_days}
                </td>
              </tr>
            ))}
            {люди.length === 0 && (
              <tr>
                <td colSpan={7} className="empty">
                  <div className="empty-title">Отсутствующих в этом окне нет</div>
                  <div className="empty-hint">
                    Ни у кого из полевых сотрудников без работ не заведено отпуска,
                    командировки, отгула или больничного на эти дни.
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ═══════════════════════════════ границы данных ═══════════════════════════════

/**
 * Раздел, который молчит про охват данных, однажды введёт в заблуждение. Здесь особенно:
 * пустая строка человека читается как «свободен», а сделка без проставленных дат в
 * календарь не попадает вовсе. Отпуска теперь учтены — но у них свой синк и своя
 * свежесть, и её тоже надо назвать вслух.
 */
function Границы({ д, сп, оговорки }: {
  д: Доска
  сп: Справочники | undefined
  оговорки: Record<string, string>
}) {
  // 'deals', а не 'D': так сущность названа в sync_state (проверено запросом 29.07.2026).
  // С кодом 'D' эта строка не печаталась ни разу — ядро искало то, чего в таблице нет.
  const синк = сп?.sync?.find((с) => с.entity === 'deals')
  // Свежесть слоя отсутствий — отдельная строка: сделки и универсальные списки Bitrix
  // зеркалятся разными синками и встают независимо друг от друга.
  const списки = сп?.sync?.find((с) => с.entity === 'bxl:elements')
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h2>Границы этих данных</h2>
      <ul className="plain-list">
        <li>
          В окне{' '}
          <b>{pluralN(д.window.days_count, 'день', 'дня', 'дней')}</b>: с{' '}
          {fmtDate(д.window.from)} по {fmtDate(д.window.to)}. Полосы, выходящие за край,
          обрезаны и помечены — настоящие даты стоят в таблице.
        </li>
        {сп && (
          <li>
            Сделок в живых стадиях —{' '}
            <b>{pluralN(сп.counts.deals, 'сделка', 'сделки', 'сделок')}</b>; исполнитель
            проставлен у {сп.counts.with_engineer}, дата начала — у {сп.counts.with_start}.
            Сделка без даты в календарь не попадает <b>вовсе</b>: показать её было бы
            враньём про день.
          </li>
        )}
        <li>
          Строк календаря в окне —{' '}
          <b>{pluralN(д.totals.jobs_in_window, 'работа', 'работы', 'работ')}</b>, из них
          показано {д.totals.jobs_shown}. Одна сделка даёт столько строк, сколько в ней
          исполнителей.
        </li>
        <li>
          Полевых сотрудников в справочнике —{' '}
          <b>{pluralN(д.totals.field_users, 'человек', 'человека', 'человек')}</b>, из них
          заняты в окне {д.totals.busy}, отсутствуют {д.totals.away}, свободны{' '}
          {д.totals.free}. Должность читается текстом из профиля Bitrix, поэтому список
          приблизителен.
        </li>
        <li>
          Отсутствий в окне —{' '}
          <b>{pluralN(д.totals.absence_rows, 'строка', 'строки', 'строк')}</b> на{' '}
          {pluralN(д.totals.absence_people, 'человека', 'человек', 'человек')}: отпуск,
          больничный, командировка, отгул.{' '}
          {д.totals.conflicts > 0
            ? `У ${pluralN(д.totals.conflicts, 'человека', 'человек', 'человек')} `
              + 'работа стоит на дни отсутствия — это расхождение двух источников, '
              + 'и правится оно в Bitrix, а не здесь.'
            : 'Работ, поставленных на дни отсутствия, в этом окне нет.'}
        </li>
        {синк && (
          <li>
            Зеркало сделок обновлено {fmtDate(синк.last_sync_at, true)}
            {сп?.last_deal_change
              ? `, последняя правка сделки этих стадий — ${fmtDate(сп.last_deal_change, true)}`
              : ''}.
          </li>
        )}
        {списки && (
          <li>
            Зеркало графика отсутствий обновлено {fmtDate(списки.last_sync_at, true)}.
            Без этой отметки «сегодня никто не в отпуске» не отличить от «слой встал
            три дня назад».
          </li>
        )}
      </ul>

      <h3>Оговорки ядра</h3>
      <ul className="plain-list">
        {Object.entries(оговорки).map(([к, текст]) => (
          <li key={к} className="muted">{текст}</li>
        ))}
      </ul>
    </div>
  )
}
